import { Gift, Shirt, Tag } from 'lucide-react'

import type { PassRewardKind, PassRewardTierItem } from '../types/pass'

import { cn } from '@/shared/lib/utils'

// Moeda e estrela são os mesmos ícones do resto da tela do passe
// (RewardItemChip, objetivos) — recompensa em valor precisa ser reconhecida
// pelo mesmo símbolo em todo lugar.
const VALUE_ICON: Partial<Record<PassRewardKind, string>> = {
  coins: '/icons/coin.svg',
  points: '/icons/star.svg'
}

const FALLBACK_ICON: Record<PassRewardKind, typeof Gift> = {
  coins: Gift,
  points: Gift,
  reward_item: Gift,
  cosmetic: Shirt,
  title: Tag
}

interface RewardVisualProps {
  item: PassRewardTierItem
  className?: string
  /** Aplica o mesmo enquadramento quando a arte é uma imagem cadastrada. */
  imageClassName?: string
  /**
   * Escreve a quantidade dentro do quadro para pontos e moedas. Ligado onde a
   * recompensa aparece sozinha (trilha, card) e desligado onde já existe um
   * rótulo por fora dizendo o valor (revelação do resgate).
   */
  showAmount?: boolean
}

/**
 * A recompensa representada por ela mesma: a arte cadastrada quando o alvo tem
 * imagem, a moeda/estrela quando é valor, e um ícone por tipo como último
 * recurso. Substitui o baú em toda a tela do passe.
 */
export function RewardVisual({
  item,
  className,
  imageClassName,
  showAmount = false
}: RewardVisualProps) {
  const valueIcon = VALUE_ICON[item.kind]
  const FallbackIcon = FALLBACK_ICON[item.kind] ?? Gift

  if (item.target_image_url) {
    return (
      <img
        src={item.target_image_url}
        alt=""
        className={cn('size-full rounded-xl object-cover', imageClassName, className)}
      />
    )
  }

  if (valueIcon) {
    if (showAmount && item.amount != null) {
      return (
        <span
          className={cn(
            'flex size-full flex-col items-center justify-center gap-0.5 leading-none',
            className
          )}
        >
          <img src={valueIcon} alt="" className="size-1/2 shrink-0 object-contain" />
          <span className="text-[0.8rem] font-black tracking-[-0.03em] text-pass-gold sm:text-[1.05rem]">
            {item.amount}
          </span>
        </span>
      )
    }

    return <img src={valueIcon} alt="" className={cn('size-3/5 object-contain', className)} />
  }

  return <FallbackIcon className={cn('size-2/5 text-pass-gold', className)} aria-hidden />
}

interface RewardStackProps {
  items: PassRewardTierItem[]
  /** Diâmetro do quadro de cada recompensa. */
  frameClassName: string
  className?: string
}

/**
 * Pilha de recompensas de um tier — um tier que entrega pontos + cosmético
 * mostra os dois. Sem itens, cai num quadro vazio para não quebrar o layout.
 */
export function RewardStack({ items, frameClassName, className }: RewardStackProps) {
  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-2', className)}>
      {items.length === 0 ? (
        <div
          className={cn(
            'flex items-center justify-center rounded-2xl bg-pass-surface-inset ring-2 ring-pass-gold/25',
            frameClassName
          )}
        >
          <Gift className="size-2/5 text-pass-gold/60" aria-hidden />
        </div>
      ) : (
        items.map(item => (
          <div
            key={item.id}
            className={cn(
              'flex items-center justify-center overflow-hidden rounded-2xl p-1.5',
              'bg-pass-surface-inset ring-2 ring-pass-gold/40',
              frameClassName
            )}
          >
            <RewardVisual item={item} showAmount />
          </div>
        ))
      )}
    </div>
  )
}
